import { ContactAddressService } from '../api/services/ContactAddressService';
import type { Routemize_DTOs_ContactAddressDTO } from '../api/models/Routemize_DTOs_ContactAddressDTO';
import type { Routemize_DTOs_ContactDTO } from '../api/models/Routemize_DTOs_ContactDTO';

export const EMPTY_CONTACT_ADDRESS_ID = '00000000-0000-0000-0000-000000000000';

export type RequestAddressFields = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export function isValidEntityId(id?: string | null): id is string {
  return !!id && id !== EMPTY_CONTACT_ADDRESS_ID;
}

export function resolveContactAddressFromDto(
  contact?: Routemize_DTOs_ContactDTO | null,
  contactAddressId?: string | null
): Routemize_DTOs_ContactAddressDTO | undefined {
  const addresses = contact?.addresses ?? [];
  if (addresses.length === 0) return undefined;

  if (isValidEntityId(contactAddressId)) {
    const matched = addresses.find((address) => address.id === contactAddressId);
    if (matched) return matched;
  }

  return addresses.find((address) => address.isActive) ?? addresses[0];
}

export function contactAddressToFields(
  address?: Routemize_DTOs_ContactAddressDTO | null
): RequestAddressFields {
  if (!address) return {};

  return {
    address1: address.addressLine1 || undefined,
    address2: address.addressLine2 || undefined,
    city: address.city || undefined,
    state: address.state || undefined,
    zipCode: address.zipCode || undefined,
  };
}

export function formatRequestAddressDisplay(
  address?: Routemize_DTOs_ContactAddressDTO | RequestAddressFields | null
): string {
  if (!address) return '';

  const parts = [
    ('addressLine1' in address ? address.addressLine1 : address.address1) || '',
    ('addressLine2' in address ? address.addressLine2 : address.address2) || '',
    address.city || '',
    address.state || '',
    address.zipCode || '',
  ].filter((part) => part.trim() !== '');

  return parts.join(', ');
}

export function mergeRequestAddressFields(
  primary: RequestAddressFields,
  fallback: RequestAddressFields
): RequestAddressFields {
  return {
    address1: primary.address1 || fallback.address1,
    address2: primary.address2 || fallback.address2,
    city: primary.city || fallback.city,
    state: primary.state || fallback.state,
    zipCode: primary.zipCode || fallback.zipCode,
  };
}

export function isRequestAddressIncomplete(fields: RequestAddressFields): boolean {
  return !fields.address1?.trim() || !fields.city?.trim() || !fields.state?.trim() || !fields.zipCode?.trim();
}

export async function fetchContactAddressFields(
  contactAddressId: string
): Promise<RequestAddressFields> {
  try {
    const address = await ContactAddressService.getApiAppContactAddressGetById(contactAddressId);
    return contactAddressToFields(address);
  } catch {
    return {};
  }
}

export async function resolveRequestAddressFields(
  contact?: Routemize_DTOs_ContactDTO | null,
  contactAddressId?: string | null
): Promise<RequestAddressFields> {
  const embeddedAddress = resolveContactAddressFromDto(contact, contactAddressId);
  let fields = contactAddressToFields(embeddedAddress);

  if (isValidEntityId(contactAddressId) && isRequestAddressIncomplete(fields)) {
    const fetchedFields = await fetchContactAddressFields(contactAddressId);
    fields = mergeRequestAddressFields(fields, fetchedFields);
  }

  return fields;
}
